import React, {useMemo} from 'react';
import cn from 'classnames';

import {EDeviceType} from 'coreClasses/device/IDevice';
import {default as Video} from 'coreComponents/VideoOld';

import Carousel, {CarouselControllerContextProvider, ConfigType} from 'brandComponents/Carousel';
import Link from 'brandComponents/Link';
import Picture from 'brandComponents/Picture';
import ShowOrHideOnBreakpoint from 'brandComponents/Responsive/ShowOrHideOnBreakpoint';

import {getProductTileCarouselConfig} from './config';
import {IProductMediaCarouselProps} from './IProductMediaCarousel';

import styles from './ProductMediaCarousel.module.scss';

const ProductMediaCarousel = ({
	productId,
	onClick,
	carouselConfig: config,
	ratio = null,
	images: images,
	videos,
	url,
	tileImageUrl,
	showPlaceholder
}: IProductMediaCarouselProps) => {
	const WrapperTag = useMemo(() => (url ? Link : 'button'), [url]);

	const wrapperProps = useMemo(() => {
		return {
			className: cn(styles['product-media__link-image-wrapper'], 'product-media__image-wrapper'),
			to: url || undefined,
			type: !url ? 'button' : undefined,
			onClick,
		};
	}, [url, onClick]);

	const generateProductMediaSlides = (images) => {
		const media = images.map((image) => ({
			key: `${productId}-${image?.getUrl()}`,
			elem: (
				<WrapperTag {...wrapperProps}>
					{/* @ts-ignore */}
					<Picture image={image} showSkeleton={showPlaceholder} ratio={ratio} />
				</WrapperTag>
			),
		}));

		if (videos?.length) {
			videos.forEach((video) => {
				media.splice(video?.position === null ? media?.length : video?.position, 0, {
					key: video?.videoId,
					elem: <Video id={video?.videoId} />,
				});
			});
		}

		return media;
	};

	const productMediaSlides = useMemo(
		() => generateProductMediaSlides(images),
		[images, videos, productId, wrapperProps]
	);
	const productMediaSlidesFiltered = useMemo(
		() => generateProductMediaSlides(images?.filter((image) => image.getUrl() !== tileImageUrl)),
		[images, tileImageUrl, videos, productId, wrapperProps]
	);

	const generateCarouselConfig = useMemo(
		() => ({
			MOBILE: getProductTileCarouselConfig(EDeviceType.MOBILE, {
				...config,
				...{navigationConfig: {enabled: productMediaSlides.length > 1}},
			} as ConfigType),
			DESKTOP: getProductTileCarouselConfig(EDeviceType.DESKTOP, {
				...config,
				...{navigationConfig: {enabled: productMediaSlidesFiltered.length > 1}},
			} as ConfigType),
		}),
		[productMediaSlides, productMediaSlidesFiltered, config],
	);

	const providerValue = useMemo(
		() => ({
			disableNavigationForOneSlide: true,
		}),
		[],
	);

	return (
		<CarouselControllerContextProvider value={providerValue}>
			{/* @ts-ignore */}
			<ShowOrHideOnBreakpoint query={`<x-large`}>
				<Carousel config={generateCarouselConfig.MOBILE}>{productMediaSlides}</Carousel>
			</ShowOrHideOnBreakpoint>
			<ShowOrHideOnBreakpoint query={`>=x-large`} classNames={styles['product-media-carousel-slides__desktop']}>
				<Carousel config={generateCarouselConfig.DESKTOP}>
					{images.length > 1 ? productMediaSlidesFiltered : productMediaSlides}
				</Carousel>
			</ShowOrHideOnBreakpoint>
		</CarouselControllerContextProvider>
	);
};

export default ProductMediaCarousel;
